/**
 * Form-aware slot orchestration for service-backed appointment creation.
 */

import { Form, type FormInstance } from 'antd';
import type { Dayjs } from 'dayjs';
import type { Service } from '@/api/mcp-appointments';
import { serviceNeedsPrimaryResource } from '@/utils/serviceBooking';
import { useServiceAvailableSlotsSchedule } from './useServiceAvailableSlotsSchedule';
import { useSlotSelectionCore, useSlotSelectionState } from './useSlotSelectionCore';

interface UseServiceAppointmentSlotSelectionProps {
  form: FormInstance;
  service: Service | null | undefined;
  visible: boolean;
  preferredDate?: Dayjs;
  initialCommitted?: boolean;
  treatAsValidSlot?: { start: Dayjs; end: Dayjs } | null;
  excludeAppointmentId?: string;
  /** When set (e.g. reschedule), fixes primary for CLIENT_SELECT_PRIMARY without form field. */
  lockedPrimaryResourceId?: string;
}

/**
 * Coordinates service slot schedule loading with form state; reselects when the service or
 * primary resource changes and clears the picked start time in that case.
 * @returns Same shape as `useAppointmentSlotSelection`, plus service readiness flags.
 */
export const useServiceAppointmentSlotSelection = ({
  form,
  service,
  visible,
  preferredDate,
  initialCommitted = false,
  treatAsValidSlot = null,
  excludeAppointmentId,
  lockedPrimaryResourceId
}: UseServiceAppointmentSlotSelectionProps) => {
  const serviceId = service?.id;
  const duration = service?.durationMinutes ?? 30;
  const needsPrimary = service ? serviceNeedsPrimaryResource(service) : false;

  const state = useSlotSelectionState({ form, duration, initialCommitted });
  const formPrimaryResourceId = Form.useWatch('primaryResourceId', form) as string | null | undefined;
  const primaryResourceId = lockedPrimaryResourceId ?? formPrimaryResourceId;

  const schedule = useServiceAvailableSlotsSchedule({
    serviceId,
    serviceTimezone: service?.timezone ?? 'UTC',
    primaryResourceId,
    needsPrimaryResource: needsPrimary,
    startTime: state.startTime,
    endTime: state.endTime,
    visibleMonth: state.slotsVisibleMonth,
    hasCommittedStartTime: state.hasCommittedStartTime,
    treatAsValidSlot,
    enabled: visible && !!service,
    excludeAppointmentId
  });

  const selection = useSlotSelectionCore({
    form,
    visible,
    preferredDate,
    duration,
    identityKeys: [serviceId, primaryResourceId],
    isSlotsQueryReady: schedule.slotsQueryReady,
    clearStartTimeOnIdentityChange: true,
    state,
    schedule
  });

  return {
    ...selection,
    resourcePolicy: undefined,
    isLoadingVisibleMonthSlots: !schedule.slotsQueryReady || schedule.isLoadingVisibleMonthSlots,
    slotsQueryReady: schedule.slotsQueryReady,
    needsPrimaryResource: needsPrimary
  };
};
